import type * as THREE from "three";

/**
 * 冻结覆盖几何依赖的源版本，按属性/布局比较而非读取全部顶点。
 * 源几何在同一对象上原位更新时也会失效；不拥有源资源、不建立全局缓存。
 */
export class ImportedModelOverlayGeometrySnapshot {
	/** 普通顶点属性的身份与版本。 */
	private readonly attributes = new Map<string, ImportedModelOverlayAttributeSnapshot>();
	/** 形变属性也参与克隆；其数组成员和版本需独立校验。 */
	private readonly morphAttributes = new Map<string, readonly ImportedModelOverlayAttributeSnapshot[]>();
	/** 索引属性身份与版本。 */
	private readonly index: ImportedModelOverlayAttributeSnapshot | null;
	/** groups 用于 primitive 子集切片，冻结数值而非仅保留可变对象引用。 */
	private readonly groups: readonly THREE.BufferGeometry["groups"][number][];
	/** 完整克隆保留绘制范围。 */
	private readonly drawStart: number;
	/** 完整克隆保留绘制数量。 */
	private readonly drawCount: number;
	/** 形变相对坐标标志。 */
	private readonly morphTargetsRelative: boolean;
	/** 源包围数据改变时不能保留覆盖层的旧裁剪范围。 */
	private readonly boundingBox: THREE.Box3 | null;
	/** 源包围球的冻结值。 */
	private readonly boundingSphere: THREE.Sphere | null;

	/** @param geometry 当前创建覆盖层所使用的源几何。 */
	public constructor(private readonly geometry: THREE.BufferGeometry) {
		for (const name in geometry.attributes) this.attributes.set(name, this.captureAttribute(geometry.attributes[name]));
		for (const name in geometry.morphAttributes) {
			this.morphAttributes.set(
				name,
				geometry.morphAttributes[name].map(attribute => this.captureAttribute(attribute))
			);
		}
		this.index = geometry.index ? this.captureAttribute(geometry.index) : null;
		this.groups = geometry.groups.map(group => ({ ...group }));
		this.drawStart = geometry.drawRange.start;
		this.drawCount = geometry.drawRange.count;
		this.morphTargetsRelative = geometry.morphTargetsRelative;
		this.boundingBox = geometry.boundingBox?.clone() ?? null;
		this.boundingSphere = geometry.boundingSphere?.clone() ?? null;
	}

	/** @param geometry 当前目标几何。 @returns 是否仍可使用已有覆盖几何。 */
	public matches(geometry: THREE.BufferGeometry): boolean {
		if (
			geometry !== this.geometry ||
			geometry.drawRange.start !== this.drawStart ||
			geometry.drawRange.count !== this.drawCount ||
			geometry.morphTargetsRelative !== this.morphTargetsRelative ||
			geometry.groups.length !== this.groups.length
		)
			return false;
		if (geometry.boundingBox ? !this.boundingBox?.equals(geometry.boundingBox) : this.boundingBox !== null) return false;
		if (geometry.boundingSphere ? !this.boundingSphere?.equals(geometry.boundingSphere) : this.boundingSphere !== null) return false;
		if (geometry.index ? !this.matchesAttribute(this.index, geometry.index) : this.index !== null) return false;
		let attributeCount = 0;
		for (const name in geometry.attributes) {
			if (!this.matchesAttribute(this.attributes.get(name), geometry.attributes[name])) return false;
			attributeCount++;
		}
		if (attributeCount !== this.attributes.size) return false;
		let morphCount = 0;
		for (const name in geometry.morphAttributes) {
			const current = geometry.morphAttributes[name];
			const previous = this.morphAttributes.get(name);
			if (!previous || previous.length !== current.length) return false;
			for (let index = 0; index < current.length; index++) if (!this.matchesAttribute(previous[index], current[index])) return false;
			morphCount++;
		}
		if (morphCount !== this.morphAttributes.size) return false;
		for (let index = 0; index < this.groups.length; index++) {
			const before = this.groups[index];
			const current = geometry.groups[index];
			if (before.start !== current.start || before.count !== current.count || before.materialIndex !== current.materialIndex) return false;
		}
		return true;
	}

	/** @param attribute 源属性。 @returns 不包含顶点副本的版本快照。 */
	private captureAttribute(attribute: THREE.BufferAttribute | THREE.InterleavedBufferAttribute): ImportedModelOverlayAttributeSnapshot {
		const data = "data" in attribute ? attribute.data : attribute;
		return {
			attribute,
			array: data.array,
			version: data.version,
			count: attribute.count,
			itemSize: attribute.itemSize,
			normalized: attribute.normalized,
			offset: "offset" in attribute ? attribute.offset : 0,
			stride: "data" in attribute ? attribute.data.stride : attribute.itemSize,
			gpuType: "gpuType" in attribute ? attribute.gpuType : undefined,
		};
	}

	/** @param snapshot 克隆时快照。 @param attribute 当前源属性。 @returns 上传版本与数据布局是否一致。 */
	private matchesAttribute(
		snapshot: ImportedModelOverlayAttributeSnapshot | null | undefined,
		attribute: THREE.BufferAttribute | THREE.InterleavedBufferAttribute
	): boolean {
		if (!snapshot || snapshot.attribute !== attribute) return false;
		const data = "data" in attribute ? attribute.data : attribute;
		// 不具备 CPU 属性数组的特殊缓冲不冒险复用，仍走原克隆路径。
		return (
			!!data.array &&
			snapshot.array === data.array &&
			snapshot.version === data.version &&
			snapshot.count === attribute.count &&
			snapshot.itemSize === attribute.itemSize &&
			snapshot.normalized === attribute.normalized &&
			snapshot.offset === ("offset" in attribute ? attribute.offset : 0) &&
			snapshot.stride === ("data" in attribute ? attribute.data.stride : attribute.itemSize) &&
			snapshot.gpuType === ("gpuType" in attribute ? attribute.gpuType : undefined)
		);
	}
}
